import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Alert,
  CircularProgress
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { getTimelineConfig, updateTimelineConfig } from '../../services/apiService';
import { isAdmin } from '../../services/authService';

/**
 * TimelineConfigEditor component for admin to edit timeline configuration
 * @param {Function} onConfigUpdated - Callback when config is updated
 * @returns {JSX.Element} TimelineConfigEditor component
 */
const TimelineConfigEditor = ({ onConfigUpdated }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [startQuarter, setStartQuarter] = useState(4);
  const [startYear, setStartYear] = useState(2025);
  const [endQuarter, setEndQuarter] = useState(2);
  const [endYear, setEndYear] = useState(2026);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getTimelineConfig();
      setConfig(data);
      setStartQuarter(data.startQuarter);
      setStartYear(data.startYear);
      setEndQuarter(data.endQuarter);
      setEndYear(data.endYear);
    } catch (err) {
      console.error('Error loading timeline config:', err);
      setError('Failed to load timeline configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validate that end is after start
    const startDate = new Date(startYear, (startQuarter - 1) * 3, 1);
    const endDate = new Date(endYear, endQuarter * 3, 0);
    
    if (endDate < startDate) {
      setError('End date must be after start date');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      await updateTimelineConfig({
        startQuarter,
        startYear,
        endQuarter,
        endYear
      });
      
      setSuccess('Timeline configuration saved successfully');
      await loadConfig();
      
      // Notify parent component to refresh data
      if (onConfigUpdated) {
        onConfigUpdated();
      }
      
      // Dispatch custom event to notify other components (like Dashboard) that timeline config changed
      window.dispatchEvent(new CustomEvent('timelineConfigUpdated', {
        detail: {
          startQuarter,
          startYear,
          endQuarter,
          endYear
        }
      }));
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving timeline config:', err);
      setError(err.message || 'Failed to save timeline configuration');
    } finally {
      setSaving(false);
    }
  };

  // Only show to admins
  if (!isAdmin()) {
    return null;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Timeline Configuration (Admin Only)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configure the timeline range for the Gantt chart and work items planning. This determines which quarters are displayed and which data is fetched from the database. All queries will be filtered to only include data within this range, improving application performance.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth>
            <InputLabel>Start Quarter</InputLabel>
            <Select
              value={startQuarter}
              label="Start Quarter"
              onChange={(e) => setStartQuarter(e.target.value)}
            >
              <MenuItem value={1}>Q1</MenuItem>
              <MenuItem value={2}>Q2</MenuItem>
              <MenuItem value={3}>Q3</MenuItem>
              <MenuItem value={4}>Q4</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth>
            <InputLabel>Start Year</InputLabel>
            <Select
              value={startYear}
              label="Start Year"
              onChange={(e) => setStartYear(e.target.value)}
            >
              {Array.from({ length: 10 }, (_, i) => 2020 + i).map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth>
            <InputLabel>End Quarter</InputLabel>
            <Select
              value={endQuarter}
              label="End Quarter"
              onChange={(e) => setEndQuarter(e.target.value)}
            >
              <MenuItem value={1}>Q1</MenuItem>
              <MenuItem value={2}>Q2</MenuItem>
              <MenuItem value={3}>Q3</MenuItem>
              <MenuItem value={4}>Q4</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth>
            <InputLabel>End Year</InputLabel>
            <Select
              value={endYear}
              label="End Year"
              onChange={(e) => setEndYear(e.target.value)}
            >
              {Array.from({ length: 10 }, (_, i) => 2020 + i).map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </Button>
      </Box>
    </Paper>
  );
};

export default TimelineConfigEditor;

